import { useState } from 'react';
import { api } from '../services/api';
import { availabilityObjToArray } from '../utils/dateUtils';

/**
 * Custom hook for managing user availability
 */
export const useAvailability = () => {
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Toggle a single time slot
  const toggleTimeSlot = (day, time) => {
    const key = `${day}-${time}`;
    setAvailability(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Handle mouse down for drag selection
  const handleMouseDown = (day, time, currentValue) => {
    const key = `${day}-${time}`;
    const newValue = !currentValue;
    
    setAvailability(prev => ({
      ...prev,
      [key]: newValue
    }));
    
    // Return drag value for use in component
    return newValue;
  };

  // Handle mouse enter for drag selection
  const handleMouseEnter = (day, time, isDragging, dragValue) => {
    if (!isDragging) return;
    
    const key = `${day}-${time}`;
    setAvailability(prev => ({
      ...prev,
      [key]: dragValue
    }));
  };

  // Clear all selected time slots
  const clearAvailability = () => {
    setAvailability({});
  };

  // Set availability from array format
  const setAvailabilityFromArray = (slots = []) => {
    const availObj = {};
    slots.forEach(slot => {
      availObj[slot] = true;
    });
    setAvailability(availObj);
  };

  // Submit availability to API
  const submitAvailability = async (userId, classType, userData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Convert availability object to array
      const availabilityArray = availabilityObjToArray(availability);
      
      if (availabilityArray.length === 0) {
        throw new Error('Please select at least one time slot');
      }
      
      // Submit to API
      const result = await api.availability.submit(
        userId,
        classType,
        availabilityArray,
        userData.name,
        userData.email,
        userData.phone
      );
      
      return result;
    } catch (err) {
      console.error('Submit availability error:', err);
      setError(err.message || 'Failed to submit availability');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get user's saved availability
  const getUserAvailability = async (userId) => {
    try {
      setLoading(true);
      setError(null);
      
      const userAvailability = await api.availability.getUserAvailability(userId);
      
      // If there are any active availabilities, set the first one
      if (userAvailability && userAvailability.length > 0) {
        setAvailabilityFromArray(userAvailability[0].availabilities);
        return userAvailability[0];
      }
      
      return null;
    } catch (err) {
      console.error('Get user availability error:', err);
      setError(err.message || 'Failed to get user availability');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    availability,
    loading,
    error,
    toggleTimeSlot,
    handleMouseDown,
    handleMouseEnter,
    clearAvailability,
    setAvailability,
    setAvailabilityFromArray,
    submitAvailability,
    getUserAvailability,
    hasSelections: Object.values(availability).some(Boolean)
  };
};